<script setup lang="ts">
import { TypedMoney } from '@commercetools/platform-sdk';
import { ProposedDelivery } from '@nuts/auto-delivery-sdk';
import { cents, from } from '@nuts/auto-delivery-sdk/dist/utils/money';
import dayjs from 'dayjs';
import { computed } from 'vue';
import { useStore } from 'vuex';

import { useDelivery } from '@/composables/useDelivery';
import money from '@/filters/money';
import { Money } from '@/utils/money';

const props = defineProps<{
  nextDelivery: ProposedDelivery;
  price: TypedMoney;
}>();

const store = useStore();

const { freeShippingThreshold } = useDelivery(store);
const totalPrice = computed(() => props.nextDelivery.totals.subtotal);

const remaining = computed(() => {
  const difference = Money.subtract(freeShippingThreshold.value, totalPrice.value);
  return Money.max(difference, from(0));
});

const freeShipping = computed(() => {
  const current = props.price;
  if (cents(remaining.value) === 0) return 'applied';
  if (cents(current) > cents(remaining.value)) return 'will-apply';
  return 'close';
});

const shortDate = computed(() => dayjs(props.nextDelivery.shipOn).format('dddd[,] MMM D'));
</script>

<template>
  <span class="my-1.5 mx-1 text-xs sm:text-sm text-neutral-600 text-center">
    <!-- Next Delivery already qualifies for Free Shipping -->
    <template v-if="freeShipping === 'applied'">
      <strong>Save on shipping!</strong>
      Add this to your <strong>{{ shortDate }}</strong>
      <span class="hidden ml-1 sm:visible sm:inline-block">Auto-Delivery</span> order
    </template>

    <!-- Next Delivery plus current selection will qualify for Free Shipping -->
    <template v-else-if="freeShipping === 'will-apply'">
      Get <strong>FREE shipping</strong> on your <strong>{{ shortDate }}</strong> order
    </template>

    <!-- Next Delivery plus current selection will get closer to Free Shipping -->
    <template v-else>
      You're <strong>{{ money(remaining) }}</strong> away from <strong>FREE shipping</strong>!
      <span class="hidden sm:visible sm:inline-block">
        Will ship with order on <strong>{{ shortDate }}</strong>
      </span>
      <span class="visible inline-block sm:hidden">
        Add to <strong>{{ shortDate }}</strong>
      </span>
    </template>
  </span>
</template>
